import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Bell, CheckCheck, Loader2, Settings2 } from 'lucide-react';
import {
  NOTIFICATION_PREVIEW_LIMIT,
  NOTIFICATION_UNREAD_BADGE_MAX,
  type Notification,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useNotifications,
  useUnreadCount,
} from '@/features/notifications/api/use-notifications';
import { NotificationItem } from '@/features/notifications/components/notification-item';
import { cn } from '@/lib/utils';

type TabKey = 'all' | 'unread' | 'critical';

export function NotificationCenter(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState<TabKey>('all');

  const unread = useUnreadCount();
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const params = useMemo(
    () => ({
      page: 1,
      pageSize: NOTIFICATION_PREVIEW_LIMIT,
      unreadOnly: tab === 'unread',
      ...(tab === 'critical' ? { severity: 'CRITICAL' as const } : {}),
    }),
    [tab],
  );

  // Die Liste wird erst beim Öffnen geladen; der Zähler kommt über den Ereignisstrom.
  const notifications = useNotifications(params, open);
  const unreadCount = unread.data?.unreadCount ?? 0;
  const entries = notifications.data?.data ?? [];

  const openNotification = (notification: Notification): void => {
    if (!notification.isRead) {
      markRead.mutate(notification.id);
    }
    if (notification.linkUrl !== null) {
      setOpen(false);
      navigate(notification.linkUrl);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative h-11 w-11 shrink-0 md:h-11 md:w-11"
          aria-label={
            unreadCount > 0
              ? t('notifications.bellWithCount', { count: unreadCount })
              : t('notifications.bell')
          }
        >
          <Bell className="h-5 w-5" aria-hidden="true" />
          {unreadCount > 0 ? (
            <span
              className={cn(
                'absolute right-1.5 top-1.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-destructive px-1 text-[10px] font-semibold text-destructive-foreground',
                'motion-safe:animate-pulse',
              )}
            >
              {unreadCount > NOTIFICATION_UNREAD_BADGE_MAX
                ? `${String(NOTIFICATION_UNREAD_BADGE_MAX)}+`
                : unreadCount}
            </span>
          ) : null}
        </Button>
      </PopoverTrigger>

      <PopoverContent
        align="end"
        side="bottom"
        avoidCollisions={false}
        className="w-[min(24rem,calc(100vw-2rem))] p-0 [&_button]:min-h-11 [&_button]:min-w-11"
      >
        <div className="flex items-center justify-between gap-2 border-b border-border px-3 py-2">
          <p className="text-sm font-medium">{t('notifications.title')}</p>
          <Button
            variant="ghost"
            size="sm"
            disabled={unreadCount === 0 || markAllRead.isPending}
            onClick={() => markAllRead.mutate()}
          >
            {markAllRead.isPending ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            ) : (
              <CheckCheck className="h-3.5 w-3.5" aria-hidden="true" />
            )}
            {t('notifications.markAllRead')}
          </Button>
        </div>

        <Tabs value={tab} onValueChange={(value) => setTab(value as TabKey)}>
          <TabsList className="mx-3 mt-2 grid w-[calc(100%-1.5rem)] grid-cols-3">
            <TabsTrigger value="all">{t('notifications.tabs.all')}</TabsTrigger>
            <TabsTrigger value="unread">{t('notifications.tabs.unread')}</TabsTrigger>
            <TabsTrigger value="critical">{t('notifications.tabs.critical')}</TabsTrigger>
          </TabsList>
        </Tabs>

        <div className="max-h-[min(28rem,60vh)] overflow-y-auto p-2">
          {notifications.isPending ? (
            <div className="flex flex-col gap-2 p-1" aria-busy="true">
              {[0, 1, 2].map((row) => (
                <Skeleton key={row} className="h-14 w-full" />
              ))}
            </div>
          ) : notifications.isError ? (
            <div className="p-6 text-center">
              <p className="text-sm">{t('notifications.error')}</p>
              <Button
                variant="outline"
                size="sm"
                className="mt-3"
                onClick={() => void notifications.refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : entries.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-sm font-medium">{t('notifications.empty.title')}</p>
              <p className="text-xs text-muted-foreground">
                {t('notifications.empty.description')}
              </p>
            </div>
          ) : (
            <ul className="flex flex-col gap-1">
              {entries.map((notification) => (
                <NotificationItem
                  key={notification.id}
                  notification={notification}
                  onSelect={openNotification}
                />
              ))}
            </ul>
          )}
        </div>

        <div className="border-t border-border p-2">
          <Button
            variant="ghost"
            size="sm"
            className="w-full justify-start"
            onClick={() => {
              setOpen(false);
              navigate('/settings/notifications');
            }}
          >
            <Settings2 className="h-3.5 w-3.5" aria-hidden="true" />
            {t('notifications.manage')}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
